import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, XCircle, Eye, Printer, History, Clock, FileText, 
  IndianRupee, TrendingUp, Factory, Package, AlertTriangle, RefreshCw,
  BarChart3, Truck, Users, Scissors, Trash2, Plus, PieChart as PieChartIcon,
  MessageCircle, Send, Check, X, Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import POPrint from '@/components/POPrint';
import PriceHistoryPopup from '@/components/PriceHistoryPopup';
import { exportToExcel } from '../utils/exportToExcel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

export default function Dashboard() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Active tabs
  const [approvalTab, setApprovalTab] = useState('po');
  const [intelligenceTab, setIntelligenceTab] = useState('sales');
  
  // Approval Hub Data
  const [pendingPOApprovals, setPendingPOApprovals] = useState([]);
  const [pendingSPOApprovals, setPendingSPOApprovals] = useState([]);
  const [pendingPaymentApprovals, setPendingPaymentApprovals] = useState([]);
  const [pendingEmpRequests, setPendingEmpRequests] = useState([]);
  const [empRequestsCount, setEmpRequestsCount] = useState(0);
  const [pendingOTPermissions, setPendingOTPermissions] = useState([]);
  const [viewEmpRequestDialog, setViewEmpRequestDialog] = useState(false);
  const [selectedEmpRequest, setSelectedEmpRequest] = useState(null);
  const [empDialogMode, setEmpDialogMode] = useState('view'); // 'view' | 'clarify' | 'reject'
  const [empDialogInput, setEmpDialogInput] = useState('');
  const [ownerCelebrations, setOwnerCelebrations] = useState([]);
  const [ownerAbsentees, setOwnerAbsentees] = useState({ count: 0, absentees: [] });
  const [ownerQuote, setOwnerQuote] = useState('');
  
  // Intelligence Data
  const [salesAnalytics, setSalesAnalytics] = useState(null);
  const [fngMonthlyData, setFngMonthlyData] = useState(null);
  const [fngPendingData, setFngPendingData] = useState(null);
  const [fngProfitabilityData, setFngProfitabilityData] = useState(null);
  const [clientPerformanceData, setClientPerformanceData] = useState(null);
  const [productionVsPlanned, setProductionVsPlanned] = useState(null);
  const [qualityReport, setQualityReport] = useState(null);
  // Variance Audit owner-dashboard metric. Read-only summary.
  const [varianceMetric, setVarianceMetric] = useState(null);
  
  // Intelligence filters
  const [fngMonths, setFngMonths] = useState(3);
  const [fngProfitPeriod, setFngProfitPeriod] = useState('ytd');
  const [fngTopN, setFngTopN] = useState(10);
  const [fngSortOrder, setFngSortOrder] = useState('top');
  const [clientPeriod, setClientPeriod] = useState('ytd');
  const [clientTopN, setClientTopN] = useState(10);
  const [clientSortOrder, setClientSortOrder] = useState('top');
  
  // Classification Performance (annual matrix)
  const [classificationMetric, setClassificationMetric] = useState('orders_qty');
  const [classificationData, setClassificationData] = useState(null);

  // F2 — Classification Monthly Matrix (new redesigned view; calls
  //       /api/fng-intelligence/classification-monthly-matrix).
  const _now = new Date();
  const _pad2 = (n) => String(n).padStart(2, '0');
  const _currentMonth = `${_now.getFullYear()}-${_pad2(_now.getMonth() + 1)}`;
  const [f2Mode, setF2Mode] = useState('quantity');                // 'quantity' | 'value'
  const [f2PeriodType, setF2PeriodType] = useState('single_month'); // 'single_month' | 'date_range'
  const [f2SelectedMonth, setF2SelectedMonth] = useState(_currentMonth);
  const [f2FromMonth, setF2FromMonth] = useState(_currentMonth);
  const [f2ToMonth, setF2ToMonth] = useState(_currentMonth);
  const [f2Matrix, setF2Matrix] = useState(null);
  const [f2Loading, setF2Loading] = useState(false);
  
  // PO Dialog states
  const [viewPODialog, setViewPODialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [printPO, setPrintPO] = useState(null);
  // Bug 9 — inline "Last Price" per line item (batched fetch on dialog open)
  const [lineItemLastPrices, setLineItemLastPrices] = useState({});
  
  // SPO Dialog states
  const [viewSPODialog, setViewSPODialog] = useState(false);
  const [selectedSPO, setSelectedSPO] = useState(null);
  
  // Clarification Dialog states
  const [clarificationDialog, setClarificationDialog] = useState({ open: false, type: null, id: null, number: null });
  const [clarificationRemarks, setClarificationRemarks] = useState('');
  
  // Payment Dialog states
  const [viewPaymentDialog, setViewPaymentDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentGrnDetails, setPaymentGrnDetails] = useState([]);
  const [loadingGrnDetails, setLoadingGrnDetails] = useState(false);
  
  // Price history popup
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceHistoryItem, setPriceHistoryItem] = useState(null);

  // Payment split dialog
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitPaymentId, setSplitPaymentId] = useState(null);
  const [splitAmount, setSplitAmount] = useState(0);
  const [splits, setSplits] = useState([{ amount: 0, due_date: '' }, { amount: 0, due_date: '' }]);
  const [splitReason, setSplitReason] = useState('');

  // CA/PA Assignment states
  const [pendingCAComplaints, setPendingCAComplaints] = useState([]);
  const [pendingPAComplaints, setPendingPAComplaints] = useState([]);
  const [capaUsers, setCapaUsers] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintWOData, setComplaintWOData] = useState(null); // Store WO data for supervisor/worker info
  const [viewComplaintDialog, setViewComplaintDialog] = useState(false);
  const [assignCAPADialog, setAssignCAPADialog] = useState(false);
  const [capaForm, setCapaForm] = useState({
    ca_description: '',
    ca_assigned_to_id: '',
    ca_assigned_to_name: '',
    ca_target_date: '',
    pa_root_cause_analysis: '',
    pa_description: '',
    pa_assigned_to_id: '',
    pa_assigned_to_name: '',
    pa_target_date: ''
  });
  const [submittingCapa, setSubmittingCapa] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  // F2 — debounced refetch of the classification matrix on any
  // mode/period change. 300ms mirrors the existing SOFulfillmentPage
  // idiom.
  useEffect(() => {
    const t = setTimeout(() => { fetchF2Matrix(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f2Mode, f2PeriodType, f2SelectedMonth, f2FromMonth, f2ToMonth]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchPendingPOApprovals(),
      fetchPendingSPOApprovals(),
      fetchPendingPaymentApprovals(),
      fetchPendingCAPA(),
      fetchPendingEmpRequests(),
      fetchPendingOTPermissions(),
      fetchSalesIntelligence(),
      fetchProductionIntelligence(),
      fetchProductsIntelligence(),
      fetchF2Matrix(),
      fetchOwnerCelebrations(),
      fetchOwnerAbsentees(),
      fetchOwnerQuote()
    ]);
    setLoading(false);
  };

  const fetchPendingPOApprovals = async () => {
    try {
      const response = await axios.get(`${API_URL}/purchase-orders/pending-approval`, { headers: getAuthHeader() });
      setPendingPOApprovals(response.data || []);
    } catch (error) {
      console.log('PO approvals not available');
    }
  };

  const fetchPendingSPOApprovals = async () => {
    try {
      const response = await axios.get(`${API_URL}/service-purchase-orders/pending-approval`, { headers: getAuthHeader() });
      setPendingSPOApprovals(response.data?.spos || []);
    } catch (error) {
      console.log('SPO approvals not available');
    }
  };

  const fetchPendingPaymentApprovals = async () => {
    try {
      const response = await axios.get(`${API_URL}/payment-requests/admin/pending`, { headers: getAuthHeader() });
      setPendingPaymentApprovals(response.data?.requests || []);
    } catch (error) {
      console.log('Payment approvals not available');
    }
  };

  const fetchPendingEmpRequests = async () => {
    try {
      const [requestsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/employee-requests?all_pending=true`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/employee-requests/pending-count`, { headers: getAuthHeader() })
      ]);
      const allRequests = requestsRes.data || [];
      setPendingEmpRequests(allRequests.filter(r => r.status === 'pending'));
      setEmpRequestsCount(allRequests.filter(r => r.status === 'pending').length);
    } catch (error) {
      console.log('Employee requests not available');
    }
  };

  const fetchPendingOTPermissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/ot-permissions/pending-approvals`, { headers: getAuthHeader() });
      setPendingOTPermissions(response.data || []);
    } catch (error) {
      console.log('OT permissions not available');
    }
  };

  const fetchPendingCAPA = async () => {
    try {
      const [assignmentsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/complaints/pending-assignments`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/users`, { headers: getAuthHeader() })
      ]);
      setPendingCAComplaints(assignmentsRes.data?.pending_ca || []);
      setPendingPAComplaints(assignmentsRes.data?.pending_pa || []);
      setCapaUsers(usersRes.data || []);
    } catch (error) {
      console.log('CA/PA data not available');
    }
  };

  const fetchOwnerCelebrations = async () => {
    try {
      const res = await axios.get(`${API_URL}/announcements/celebrations`, { headers: getAuthHeader() });
      setOwnerCelebrations(res.data || []);
    } catch (error) {
      console.log('Celebrations not available');
    }
  };

  const fetchOwnerAbsentees = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests/todays-absentees`, { headers: getAuthHeader() });
      setOwnerAbsentees(res.data || { count: 0, absentees: [] });
    } catch (error) {
      console.log('Absentees not available');
    }
  };

  const fetchOwnerQuote = async () => {
    try {
      const res = await axios.get(`${API_URL}/employee-requests/quote-of-the-day`, { headers: getAuthHeader() });
      setOwnerQuote(res.data?.quote || '');
    } catch (error) {
      console.log('Quote not available');
    }
  };

  // Fetch WO data to get supervisor/worker info for a complaint
  const fetchComplaintWOData = async (complaint) => {
    setComplaintWOData(null); // Reset
    if (!complaint?.work_order_id) return;
    
    try {
      const response = await axios.get(`${API_URL}/work-orders/${complaint.work_order_id}`, { headers: getAuthHeader() });
      const wo = response.data;
      
      // Find the matching process and extract supervisor/worker info
      if (wo && complaint.process_name) {
        const process = wo.production_processes?.find(p => p.process_name === complaint.process_name);
        if (process && process.jobs?.length > 0) {
          // Get the first job's issued_by_name (supervisor) and workers
          const firstJob = process.jobs[0];
          setComplaintWOData({
            supervisor_name: firstJob.issued_by_name || null,
            worker_name: firstJob.workers?.length > 0 ? (firstJob.workers[0].employee_name || firstJob.workers[0].worker_name) : (firstJob.employee_name || firstJob.worker_name || null),
            additional_workers: firstJob.workers?.length > 1 ? firstJob.workers.slice(1).map(w => w.employee_name || w.worker_name) : []
          });
          return;
        }
      }
      setComplaintWOData({ supervisor_name: null, worker_name: null, additional_workers: [] });
    } catch (error) {
      console.log('Could not fetch WO data for complaint');
      setComplaintWOData({ supervisor_name: null, worker_name: null, additional_workers: [] });
    }
  };

  const fetchSalesIntelligence = async () => {
    try {
      const [sales, monthly, pending] = await Promise.all([
        axios.get(`${API_URL}/reports/sales-analytics`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/fng-intelligence/monthly-comparison?months=${fngMonths}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/fng-intelligence/pending-summary`, { headers: getAuthHeader() })
      ]);
      setSalesAnalytics(sales.data);
      setFngMonthlyData(monthly.data);
      setFngPendingData(pending.data);
    } catch (error) {
      console.error('Error fetching sales intelligence:', error);
    }
  };

  const fetchProductionIntelligence = async () => {
    try {
      const [planned, quality, variance] = await Promise.all([
        axios.get(`${API_URL}/reports/production-vs-planned`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/reports/quality-problems`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/variance-entries/owner-dashboard-metric`, { headers: getAuthHeader() })
          .catch(() => ({ data: null })),  // tolerate 403 for non-module users
      ]);
      setProductionVsPlanned(planned.data);
      setQualityReport(quality.data);
      setVarianceMetric(variance.data);
    } catch (error) {
      console.error('Error fetching production intelligence:', error);
    }
  };

  const fetchProductsIntelligence = async () => {
    try {
      const [profitability, clients, classData] = await Promise.all([
        axios.get(`${API_URL}/fng-intelligence/profitability-by-classification?period=${fngProfitPeriod}&top_n=${fngTopN}&sort_order=${fngSortOrder}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/fng-intelligence/client-performance?period=${clientPeriod}&top_n=${clientTopN}&sort_order=${clientSortOrder}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/fng-intelligence/monthly-comparison?months=12`, { headers: getAuthHeader() })
      ]);
      setFngProfitabilityData(profitability.data);
      setClientPerformanceData(clients.data);
      setClassificationData(classData.data);
    } catch (error) {
      console.error('Error fetching products intelligence:', error);
    }
  };

  // F2 — fetch the classification monthly matrix on mode/period change.
  const fetchF2Matrix = async () => {
    setF2Loading(true);
    try {
      const params = { mode: f2Mode, period_type: f2PeriodType };
      if (f2PeriodType === 'single_month') {
        if (f2SelectedMonth) params.selected_month = f2SelectedMonth;
      } else {
        params.from_month = f2FromMonth;
        params.to_month = f2ToMonth;
      }
      const r = await axios.get(`${API_URL}/fng-intelligence/classification-monthly-matrix`,
        { params, headers: getAuthHeader() });
      setF2Matrix(r.data);
    } catch (err) {
      console.error('F2 matrix fetch failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to load classification matrix');
      setF2Matrix(null);
    } finally {
      setF2Loading(false);
    }
  };

  // F2 — Excel export. One flat row per classification + totals row.
  const handleF2Export = () => {
    if (!f2Matrix || !f2Matrix.rows) { toast.info('Nothing to export'); return; }
    const months = f2Matrix.months || [];
    const rows = (f2Matrix.rows || []).map((row) => {
      const obj = { Classification: row.classification };
      months.forEach((m) => {
        const c = row.cells.find((cc) => cc.month_key === m.month_key) || { order: 0, production: 0, dispatched: 0 };
        obj[`${m.month_label} - Order`]      = c.order || '';
        obj[`${m.month_label} - Production`] = c.production || '';
        obj[`${m.month_label} - Dispatched`] = c.dispatched || '';
      });
      obj['Pending Production'] = row.pending_production || '';
      return obj;
    });
    const tCells = f2Matrix.totals?.cells || [];
    const totalsRow = { Classification: 'Totals' };
    months.forEach((m) => {
      const c = tCells.find((cc) => cc.month_key === m.month_key) || { order: 0, production: 0, dispatched: 0 };
      totalsRow[`${m.month_label} - Order`]      = c.order || '';
      totalsRow[`${m.month_label} - Production`] = c.production || '';
      totalsRow[`${m.month_label} - Dispatched`] = c.dispatched || '';
    });
    totalsRow['Pending Production'] = f2Matrix.totals?.pending_production || '';
    rows.push(totalsRow);
    let periodDescriptor;
    if (f2PeriodType === 'single_month') {
      periodDescriptor = (f2SelectedMonth || 'current').replace('-', '_').toLowerCase();
    } else {
      periodDescriptor = `${(f2FromMonth || '').replace('-', '_')}_to_${(f2ToMonth || '').replace('-', '_')}`.toLowerCase();
    }
    const filename = `products_classification_${f2Mode}_${periodDescriptor}.xlsx`;
    exportToExcel(rows, 'Classification Matrix', filename);
  };

  const handlePOApproval = async (poId, action, rejectionReason = '') => {
    try {
      const response = await axios.post(`${API_URL}/purchase-orders/${poId}/approve`, {
        action,
        rejection_reason: rejectionReason
      }, { headers: getAuthHeader() });
      toast.success(response.data.message);
      fetchPendingPOApprovals();
      setViewPODialog(false);
      setLineItemLastPrices({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  // Bug 9 — open PO details dialog and batch-fetch last price per line item
  const openPODetails = async (po) => {
    setSelectedPO(po);
    setViewPODialog(true);
    setLineItemLastPrices({});
    if (po?.line_items && po.line_items.length > 0) {
      try {
        const results = await Promise.all(
          po.line_items.map(async (li) => {
            if (!li.item_id) return [li.item_id, null];
            try {
              const params = new URLSearchParams();
              if (po.supplier_id) params.append('supplier_id', po.supplier_id);
              if (po.supplier_name) params.append('supplier_name', po.supplier_name);
              const res = await axios.get(
                `${API_URL}/store/price-history/${li.item_id}?${params.toString()}`,
                { headers: getAuthHeader() }
              );
              const lastSame = res.data?.last_same_supplier;
              const fallbackAvg = res.data?.avg_all_suppliers;
              return [li.item_id, {
                price: lastSame?.price ?? fallbackAvg?.price ?? null,
                source: lastSame ? 'same_supplier' : (fallbackAvg ? 'avg_all' : null),
                date: lastSame?.date ?? null,
              }];
            } catch {
              return [li.item_id, null];
            }
          })
        );
        const map = {};
        results.forEach(([k, v]) => { if (k) map[k] = v; });
        setLineItemLastPrices(map);
      } catch (e) {
        // silent — column just renders '-' when missing
      }
    }
  };

  const handleSPOApproval = async (spoId, action, rejectionReason = '') => {
    try {
      const response = await axios.post(`${API_URL}/service-purchase-orders/${spoId}/approve`, {
        action,
        rejection_reason: rejectionReason
      }, { headers: getAuthHeader() });
      toast.success(response.data.message);
      fetchPendingSPOApprovals();
      fetchPendingPaymentApprovals(); // Refresh payment approvals if advance payment created
      setViewSPODialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  // Clarification handlers
  const openClarificationDialog = (type, id, number) => {
    setClarificationDialog({ open: true, type, id, number });
    setClarificationRemarks('');
  };

  const handleRequestClarification = async () => {
    if (!clarificationRemarks.trim()) {
      toast.error('Please enter clarification remarks');
      return;
    }
    
    const { type, id } = clarificationDialog;
    const endpoint = type === 'po' 
      ? `${API_URL}/purchase-orders/${id}/request-clarification`
      : `${API_URL}/service-purchase-orders/${id}/request-clarification`;
    
    try {
      const response = await axios.post(endpoint, {
        remarks: clarificationRemarks.trim()
      }, { headers: getAuthHeader() });
      toast.success(response.data.message);
      setClarificationDialog({ open: false, type: null, id: null, number: null });
      setClarificationRemarks('');
      
      if (type === 'po') {
        fetchPendingPOApprovals();
        setViewPODialog(false);
        setLineItemLastPrices({});
      } else {
        fetchPendingSPOApprovals();
        setViewSPODialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request clarification');
    }
  };

  const handlePaymentApproval = async (requestId, action, data = {}, status = 'submitted') => {
    try {
      // Use different endpoint for modification_requested status
      const endpoint = status === 'modification_requested' 
        ? `${API_URL}/payment-requests/${requestId}/approve-modification`
        : `${API_URL}/payment-requests/${requestId}/approve`;
      
      await axios.patch(endpoint, {
        decision: action,
        ...data
      }, { headers: getAuthHeader() });
      toast.success(`Payment request ${action}d successfully`);
      fetchPendingPaymentApprovals();
      setViewPaymentDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleEmpRequestApproval = async (requestId, action, reason = null) => {
    try {
      const endpoint = action === 'approve' 
        ? `${API_URL}/employee-requests/${requestId}/approve`
        : `${API_URL}/employee-requests/${requestId}/reject`;
      
      const payload = action === 'reject' ? { reason } : {};
      
      await axios.patch(endpoint, payload, { headers: getAuthHeader() });
      toast.success(`Request ${action}d successfully`);
      fetchPendingEmpRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  const handleEmpRequestClarification = async (requestId, message) => {
    if (!message || !message.trim()) { toast.error('Please enter a clarification message'); return; }
    try {
      await axios.patch(
        `${API_URL}/employee-requests/${requestId}/clarify`,
        { message: message.trim() },
        { headers: getAuthHeader() }
      );
      toast.success('Clarification requested');
      setViewEmpRequestDialog(false);
      setSelectedEmpRequest(null);
      setEmpDialogMode('view');
      setEmpDialogInput('');
      fetchPendingEmpRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request clarification');
    }
  };

  // Fetch GRN details when viewing a payment request
  const fetchPaymentGrnDetails = async (requestId) => {
    setLoadingGrnDetails(true);
    setPaymentGrnDetails([]);
    try {
      const response = await axios.get(`${API_URL}/payment-requests/${requestId}`, { headers: getAuthHeader() });
      if (response.data?.grn_details) {
        setPaymentGrnDetails(response.data.grn_details);
      }
    } catch (error) {
      console.error('Failed to fetch GRN details:', error);
    } finally {
      setLoadingGrnDetails(false);
    }
  };

  // Open payment view dialog and fetch GRN details
  const openPaymentViewDialog = (req) => {
    setSelectedPayment(req);
    setViewPaymentDialog(true);
    // Fetch GRN details if this payment has linked GRNs
    if (req.source_grn_id || (req.linked_grns && req.linked_grns.length > 0)) {
      fetchPaymentGrnDetails(req.id);
    } else {
      setPaymentGrnDetails([]);
    }
  };

  // Handle payment splitting
  const handleSplitPayment = async () => {
    if (!splitPaymentId) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/payment-requests/${splitPaymentId}/split`,
        {
          splits: splits.map(s => ({
            amount: s.amount,
            due_date: s.due_date
          })),
          reason: splitReason
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Payment split into ${splits.length} parts successfully`);
      
      // Reset state
      setSplitDialogOpen(false);
      setSplitPaymentId(null);
      setSplitAmount(0);
      setSplits([{ amount: 0, due_date: '' }, { amount: 0, due_date: '' }]);
      setSplitReason('');
      
      // Close view dialog and refresh
      setViewPaymentDialog(false);
      fetchPendingPaymentApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split payment');
    }
  };

  // Count badges
  const poCount = pendingPOApprovals.length;
  const spoCount = pendingSPOApprovals.length;
  const paymentCount = pendingPaymentApprovals.length;
  const capaCount = pendingCAComplaints.length; // Only pending CA/PA assignments (before any assignment)
  const otCount = pendingOTPermissions.length;
  const totalApprovals = poCount + spoCount + paymentCount + capaCount + empRequestsCount + otCount;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAllData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Quote of the Day */}
        {ownerQuote && (
          <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-base md:text-lg italic text-amber-900/90 font-medium leading-relaxed">&ldquo;{ownerQuote}&rdquo;</p>
          </div>
        )}

        {/* Today's Celebrations + Absentees */}
        {(ownerCelebrations.length > 0 || ownerAbsentees.count > 0) && (
          <div className="flex items-start gap-4">
            {ownerCelebrations.length > 0 && (
              <Card className="bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200 flex-1">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">🎉</span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-purple-800 text-sm">Today&apos;s Celebrations</h3>
                      <div className="mt-1.5 space-y-1">
                        {ownerCelebrations.map((c, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <span className="text-base">{c.icon}</span>
                            <span className="font-medium text-purple-900">{c.name}</span>
                            {c.department && <span className="text-purple-600 text-xs">({c.department})</span>}
                            <span className="text-purple-700 text-xs">- {c.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {ownerAbsentees.count > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors cursor-pointer border border-gray-200 whitespace-nowrap mt-1">
                    <Users className="w-3.5 h-3.5" />
                    Absent Today: {ownerAbsentees.count}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="end">
                  <p className="text-sm font-semibold mb-2">Today&apos;s Absentees</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {ownerAbsentees.absentees.map((a, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                        <span className="font-medium">{a.name}</span>
                        <span className="text-muted-foreground">{a.type}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}

        {/* SECTION 1: APPROVAL HUB */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                <CardTitle>Approval Hub</CardTitle>
              </div>
              {totalApprovals > 0 && (
                <Badge variant="destructive" className="text-sm">
                  {totalApprovals} Pending
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={approvalTab} onValueChange={setApprovalTab}>
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="po" className="relative">
                  PO Approvals
                  {poCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {poCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="spo" className="relative">
                  Service PO
                  {spoCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {spoCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="payment" className="relative">
                  Payment Approvals
                  {paymentCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {paymentCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="leave" className="relative">
                  Emp Requests
                  {(empRequestsCount + otCount) > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {empRequestsCount + otCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="capa" className="relative">
                  CA/PA
                  {capaCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                      {capaCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* PO Approvals Tab */}
              <TabsContent value="po">
                {poCount === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending PO approvals</p>
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>PO Number</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead>Submitted By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingPOApprovals.map((po) => (
                          <TableRow key={po.id}>
                            <TableCell className="font-medium">{po.po_number}</TableCell>
                            <TableCell>{po.supplier_name}</TableCell>
                            <TableCell>{po.submitted_by_name}</TableCell>
                            <TableCell>{new Date(po.po_date).toLocaleDateString('en-GB')}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(po.total_amount)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openPODetails(po)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handlePOApproval(po.id, 'approve')}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handlePOApproval(po.id, 'reject', reason);
                                }}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* Service PO Approvals Tab */}
              <TabsContent value="spo">
                {spoCount === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending Service PO approvals</p>
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>SPO Number</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead>Service Description</TableHead>
                          <TableHead>Submitted By</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Advance</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingSPOApprovals.map((spo) => (
                          <TableRow key={spo.id}>
                            <TableCell className="font-medium">{spo.spo_number}</TableCell>
                            <TableCell>{spo.supplier_name}</TableCell>
                            <TableCell className="max-w-[200px] truncate">{spo.service_description}</TableCell>
                            <TableCell>{spo.submitted_by_name}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(spo.grand_total)}
                            </TableCell>
                            <TableCell className="text-right">
                              {spo.advance_requested ? (
                                <span className="text-orange-600 font-medium">{formatCurrency(spo.advance_amount)}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="ghost" onClick={() => { setSelectedSPO(spo); setViewSPODialog(true); }}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleSPOApproval(spo.id, 'approve')}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handleSPOApproval(spo.id, 'reject', reason);
                                }}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* Payment Approvals Tab */}
              <TabsContent value="payment">
                {paymentCount === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending payment approvals</p>
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Request #</TableHead>
                          <TableHead>Supplier</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Requested By</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingPaymentApprovals.map((req) => (
                          <TableRow key={req.id} className={req.status === 'modification_requested' ? 'bg-amber-50' : ''}>
                            <TableCell className="font-medium">{req.request_number}</TableCell>
                            <TableCell>{req.supplier_name}</TableCell>
                            <TableCell>
                              <Badge variant={req.payment_type === 'advance' ? 'default' : 'secondary'}>
                                {req.payment_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {req.status === 'modification_requested' ? (
                                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                                  Modification
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  New
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>{req.created_by_name}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(req.amount_requested)}
                            </TableCell>
                            <TableCell>
                              {req.payment_due_date ? new Date(req.payment_due_date).toLocaleDateString('en-GB') : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openPaymentViewDialog(req)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handlePaymentApproval(req.id, 'approve', { approved_amount: req.amount_requested }, req.status)}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                                  const reason = prompt('Rejection reason:');
                                  if (reason) handlePaymentApproval(req.id, 'reject', { admin_remarks: reason }, req.status);
                                }}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {/* Employee Requests Tab */}
              <TabsContent value="leave">
                {(empRequestsCount === 0 && otCount === 0) ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending employee requests</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* OT Permission Requests Section */}
                    {otCount > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-600" />
                          OT Permission Requests ({otCount})
                        </h4>
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-amber-50/50">
                                <TableHead>Request #</TableHead>
                                <TableHead>Unit</TableHead>
                                <TableHead>OT Date</TableHead>
                                <TableHead>Time</TableHead>
                                <TableHead>Workers</TableHead>
                                <TableHead className="text-right">Est. Cost</TableHead>
                                <TableHead>Requested By</TableHead>
                                <TableHead className="text-center">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pendingOTPermissions.map((ot) => (
                                <TableRow key={ot.id}>
                                  <TableCell className="font-medium text-blue-600">{ot.request_number}</TableCell>
                                  <TableCell>{ot.unit_name}</TableCell>
                                  <TableCell>{new Date(ot.ot_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                                  <TableCell>{ot.from_time} - {ot.to_time} ({ot.estimated_hours}h)</TableCell>
                                  <TableCell>{ot.worker_count}</TableCell>
                                  <TableCell className="text-right">₹{ot.estimated_cost?.toLocaleString('en-IN')}</TableCell>
                                  <TableCell>{ot.requested_by_name}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={async () => {
                                          try {
                                            await axios.patch(`${API_URL}/ot-permissions/${ot.id}/approve`, {}, { headers: getAuthHeader() });
                                            toast.success('OT Permission approved');
                                            fetchPendingOTPermissions();
                                          } catch (error) {
                                            toast.error(error.response?.data?.detail || 'Failed to approve');
                                          }
                                        }}
                                        title="Approve"
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={async () => {
                                          const reason = prompt('Enter rejection reason:');
                                          if (reason) {
                                            try {
                                              await axios.patch(`${API_URL}/ot-permissions/${ot.id}/reject`, { reason }, { headers: getAuthHeader() });
                                              toast.success('OT Permission rejected');
                                              fetchPendingOTPermissions();
                                            } catch (error) {
                                              toast.error(error.response?.data?.detail || 'Failed to reject');
                                            }
                                          }
                                        }}
                                        title="Reject"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Employee Requests Section */}
                    {empRequestsCount > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          Leave & Other Requests ({empRequestsCount})
                        </h4>
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30">
                                <TableHead>Request #</TableHead>
                                <TableHead>Employee</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Details</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-center">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pendingEmpRequests.map((req) => (
                                <TableRow key={req.id}>
                                  <TableCell>
                                    <Badge variant="outline" className="font-mono text-xs">{req.request_number}</Badge>
                                  </TableCell>
                                  <TableCell className="font-medium">{req.employee_name}</TableCell>
                                  <TableCell>
                                    <Badge variant="secondary" className="text-xs">
                                      {req.type === 'regular_leave' && 'Regular Leave'}
                                      {req.type === 'emergency_leave' && 'Emergency Leave'}
                                      {req.type === 'paid_leave' && 'Paid Leave'}
                                      {req.type === 'overtime' && 'Overtime'}
                                      {req.type === 'reimbursement' && 'Reimbursement'}
                                      {req.type === 'permission' && 'Short Leave'}
                                      {req.type === 'advance' && 'Advance'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {req.type?.includes('leave') && (
                                      <span>{new Date(req.from_date).toLocaleDateString('en-GB')} - {new Date(req.to_date).toLocaleDateString('en-GB')} ({req.num_days} days)</span>
                                    )}
                                    {req.type === 'overtime' && (
                                      <span>{req.from_time} - {req.to_time} ({req.hours} hrs)</span>
                                    )}
                                    {req.type === 'reimbursement' && (
                                      <span>{req.items?.length} items | ₹{req.total_amount?.toLocaleString()}</span>
                                    )}
                                    {req.type === 'permission' && (
                                      <span>{new Date(req.date).toLocaleDateString('en-GB')} | {req.from_time} - {req.to_time}</span>
                                    )}
                                    {req.type === 'advance' && (
                                      <span>{req.advance_type === 'salary' ? 'Salary' : 'Travel'} | ₹{req.amount?.toLocaleString()}</span>
                                    )}
                                    {req.reason && <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]" title={req.reason}>{req.reason}</p>}
                                  </TableCell>
                                  <TableCell className="text-sm">{new Date(req.created_at).toLocaleDateString('en-GB')}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center justify-center gap-1">
                                      <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleEmpRequestApproval(req.id, 'approve')} title="Approve">
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="text-gray-600" onClick={() => { setSelectedEmpRequest(req); setEmpDialogMode('view'); setEmpDialogInput(''); setViewEmpRequestDialog(true); }} title="View Details">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setSelectedEmpRequest(req); setEmpDialogMode('reject'); setEmpDialogInput(''); setViewEmpRequestDialog(true); }} title="Reject">
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="capa">
                {capaCount === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>No pending CA/PA assignments</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Pending CA/PA Assignment Section */}
                    {pendingCAComplaints.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          Pending CA/PA Assignment ({pendingCAComplaints.length})
                        </h4>
                        <div className="border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-red-50/50">
                                <TableHead>Complaint #</TableHead>
                                <TableHead>Title</TableHead>
                                <TableHead className="text-center">Severity</TableHead>
                                <TableHead className="text-center">Source</TableHead>
                                <TableHead>Raised By</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-center">View</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pendingCAComplaints.map((complaint) => (
                                <TableRow key={complaint.id}>
                                  <TableCell>
                                    <Badge variant="outline" className="font-mono text-xs">
                                      {complaint.complaint_no}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="max-w-[250px] truncate font-medium" title={complaint.title}>
                                    {complaint.title}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant="outline" className={
                                      complaint.severity === 'critical' ? 'bg-red-100 text-red-700 border-red-300' :
                                      complaint.severity === 'major' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                      'bg-yellow-100 text-yellow-700 border-yellow-300'
                                    }>
                                      {complaint.severity === 'critical' ? '🔴' : complaint.severity === 'major' ? '🟠' : '🟡'} {complaint.severity}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Badge variant="secondary" className="text-xs capitalize">{complaint.source}</Badge>
                                  </TableCell>
                                  <TableCell className="text-sm">{complaint.raised_by_name}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {complaint.raised_date ? new Date(complaint.raised_date).toLocaleDateString('en-GB') : '-'}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      className="border-red-300 text-red-700 hover:bg-red-50"
                                      onClick={() => { 
                                        setSelectedComplaint(complaint); 
                                        fetchComplaintWOData(complaint);
                                        setCapaForm({
                                          ca_description: '', ca_assigned_to_id: '', ca_assigned_to_name: '', ca_target_date: '',
                                          pa_root_cause_analysis: '', pa_description: '', pa_assigned_to_id: '', pa_assigned_to_name: '', pa_target_date: ''
                                        });
                                        setAssignCAPADialog(true); 
                                      }}
                                    >
                                      <Eye className="w-4 h-4 mr-1" /> View & Assign
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* SECTION 2: INTELLIGENCE REPORTS */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <CardTitle>Intelligence Reports</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={intelligenceTab} onValueChange={setIntelligenceTab}>
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="sales" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Sales
                </TabsTrigger>
                <TabsTrigger value="production" className="flex items-center gap-2">
                  <Factory className="w-4 h-4" />
                  Production
                </TabsTrigger>
                <TabsTrigger value="products" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Products
                </TabsTrigger>
                <TabsTrigger value="gp" className="flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4" />
                  GP Analytics
                </TabsTrigger>
              </TabsList>

              {/* GP Analytics Quick Access Tab */}
              <TabsContent value="gp" className="space-y-4">
                <div className="border rounded-lg p-6 bg-gradient-to-br from-emerald-50 to-teal-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800">Gross Profit Intelligence</h3>
                      <p className="text-sm text-emerald-600 mt-1">
                        Analyze GP by Month, Client, and Product Classification
                      </p>
                      <div className="flex gap-4 mt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-700">Monthly</p>
                          <p className="text-xs text-emerald-600">FY Breakdown</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-700">Client</p>
                          <p className="text-xs text-emerald-600">Top Performers</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-700">Product</p>
                          <p className="text-xs text-emerald-600">By Classification</p>
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/reports/gp-analytics')}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="gp-analytics-btn"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Open GP Dashboard
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Sales Intelligence Tab */}
              <TabsContent value="sales" className="space-y-4">
                {/* Monthly Comparison */}
                {fngMonthlyData && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Monthly Comparison</h3>
                      <Select value={String(fngMonths)} onValueChange={(v) => { setFngMonths(Number(v)); fetchSalesIntelligence(); }}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 Months</SelectItem>
                          <SelectItem value="6">6 Months</SelectItem>
                          <SelectItem value="12">12 Months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={fngMonthlyData.monthly_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month_label" />
                          <YAxis 
                            tickFormatter={(value) => {
                              if (value >= 10000000) return `₹${(value/10000000).toFixed(0)} Cr`;
                              if (value >= 100000) return `₹${(value/100000).toFixed(0)} L`;
                              return `₹${value}`;
                            }} 
                          />
                          <Tooltip 
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                // Sort payload to show Orders, Production, Dispatches
                                const orderedPayload = [
                                  payload.find(p => p.dataKey === 'orders_value'),
                                  payload.find(p => p.dataKey === 'production_value'),
                                  payload.find(p => p.dataKey === 'dispatch_value')
                                ].filter(Boolean);
                                
                                const formatValue = (value) => {
                                  if (value >= 10000000) return `₹${(value/10000000).toFixed(2)} Cr`;
                                  return `₹${(value/100000).toFixed(2)} L`;
                                };
                                
                                return (
                                  <div className="bg-white p-2 border rounded shadow-lg">
                                    <p className="font-semibold mb-1">{label}</p>
                                    {orderedPayload.map((entry, index) => (
                                      <p key={index} style={{ color: entry.fill }}>
                                        {entry.name}: {formatValue(entry.value)}
                                      </p>
                                    ))}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend 
                            content={() => (
                              <div className="flex justify-center gap-6 pt-2">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#8b5cf6' }}></div>
                                  <span className="text-sm">Orders</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#f59e0b' }}></div>
                                  <span className="text-sm">Production</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }}></div>
                                  <span className="text-sm">Dispatches</span>
                                </div>
                              </div>
                            )}
                          />
                          <Bar dataKey="orders_value" name="Orders" fill="#8b5cf6" />
                          <Bar dataKey="production_value" name="Production" fill="#f59e0b" />
                          <Bar dataKey="dispatch_value" name="Dispatches" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Pending Summary */}
                {fngPendingData && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-4">Pending Summary</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-violet-50 rounded-lg">
                        <p className="text-2xl font-bold text-violet-700">
                          {(fngPendingData.total_pending_dispatch_value || 0) >= 10000000 
                            ? `₹${((fngPendingData.total_pending_dispatch_value || 0)/10000000).toFixed(2)} Cr`
                            : `₹${((fngPendingData.total_pending_dispatch_value || 0)/100000).toFixed(2)} L`}
                        </p>
                        <p className="text-sm text-violet-600">Total Pending Dispatches</p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <p className="text-2xl font-bold text-orange-700">
                          {(fngPendingData.this_month_dispatch_value || 0) >= 10000000 
                            ? `₹${((fngPendingData.this_month_dispatch_value || 0)/10000000).toFixed(2)} Cr`
                            : `₹${((fngPendingData.this_month_dispatch_value || 0)/100000).toFixed(2)} L`}
                        </p>
                        <p className="text-sm text-orange-600">Pending Dispatches (This Month)</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700">
                          {(fngPendingData.next_month_dispatch_value || 0) >= 10000000 
                            ? `₹${((fngPendingData.next_month_dispatch_value || 0)/10000000).toFixed(2)} Cr`
                            : `₹${((fngPendingData.next_month_dispatch_value || 0)/100000).toFixed(2)} L`}
                        </p>
                        <p className="text-sm text-blue-600">Pending Dispatches (Next Month)</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">
                          {(fngPendingData.total_pending_production_value || 0) >= 10000000 
                            ? `₹${((fngPendingData.total_pending_production_value || 0)/10000000).toFixed(2)} Cr`
                            : `₹${((fngPendingData.total_pending_production_value || 0)/100000).toFixed(2)} L`}
                        </p>
                        <p className="text-sm text-green-600">Total Pending Production</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sales Analytics Chart */}
                {salesAnalytics?.monthly_dispatch && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-4">Dispatch Value Trend (12 Months)</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={salesAnalytics.monthly_dispatch}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip formatter={(value) => `₹${(value/100000).toFixed(1)}L`} />
                          <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="#8b5cf680" name="Dispatch Value" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Production Intelligence Tab */}
              <TabsContent value="production" className="space-y-4">
                {/* Variance Audit summary tile — pending count + total amount.
                    Click navigates to /variance-audit. */}
                {varianceMetric && (
                  <button
                    type="button"
                    onClick={() => navigate('/variance-audit')}
                    className="w-full text-left border rounded-lg p-4 bg-amber-50 hover:bg-amber-100 transition-colors"
                    data-testid="va-dashboard-tile"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          Pending Variances
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          BOM-vs-actual material cost deltas awaiting approval
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-amber-700">
                          {varianceMetric.pending_count}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ₹{Number(varianceMetric.total_pending_variance_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </button>
                )}

                {/* Production vs Planned */}
                {productionVsPlanned && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-4">Production vs Planned</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={productionVsPlanned.daily_data?.slice(-14) || []}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="planned" name="Planned" fill="#94a3b8" />
                          <Bar dataKey="actual" name="Actual" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Quality Report */}
                {qualityReport && qualityReport.problems?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      Quality Issues
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Process</TableHead>
                          <TableHead>Issue</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {qualityReport.problems.slice(0, 5).map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{p.process_name}</TableCell>
                            <TableCell>{p.problem}</TableCell>
                            <TableCell className="text-right font-medium">{p.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {!productionVsPlanned && !qualityReport && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Factory className="w-12 h-12 mx-auto mb-2" />
                    <p>No production data available</p>
                  </div>
                )}
              </TabsContent>

              {/* Products Intelligence Tab */}
              <TabsContent value="products" className="space-y-4">
                {/* F2 — Classification Monthly Matrix (redesigned).
                    Replaces the previous single-dropdown Classification
                    Performance pivot. Same semantic columns (Order /
                    Production / Dispatched) but with period controls,
                    By-Quantity/By-Value mode toggle, Pending-Production
                    snapshot column, and xlsx export. */}
                <div className="border rounded-lg p-4" data-testid="f2-classification-matrix">
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <h3 className="font-semibold">Classification Performance</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {f2PeriodType === 'single_month'
                          ? 'Showing selected month + 2 preceding months.'
                          : 'Showing all months in the selected range.'}
                        {f2Matrix?.meta && (f2Matrix.meta.unresolved_production_refs > 0 || f2Matrix.meta.unresolved_dispatch_refs > 0) && (
                          <span className="ml-2 text-amber-700">
                            · unresolved refs: prod={f2Matrix.meta.unresolved_production_refs}, disp={f2Matrix.meta.unresolved_dispatch_refs}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* Mode toggle */}
                      <div className="inline-flex rounded-md border overflow-hidden" data-testid="f2-mode-toggle">
                        <button
                          type="button"
                          onClick={() => setF2Mode('quantity')}
                          className={`px-3 py-1 text-sm ${f2Mode === 'quantity' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
                          data-testid="f2-mode-qty"
                        >By Qty</button>
                        <button
                          type="button"
                          onClick={() => setF2Mode('value')}
                          className={`px-3 py-1 text-sm ${f2Mode === 'value' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
                          data-testid="f2-mode-value"
                        >By Value</button>
                      </div>
                      {/* Period type toggle */}
                      <div className="inline-flex rounded-md border overflow-hidden" data-testid="f2-period-toggle">
                        <button
                          type="button"
                          onClick={() => setF2PeriodType('single_month')}
                          className={`px-3 py-1 text-sm ${f2PeriodType === 'single_month' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
                          data-testid="f2-period-single"
                        >Single Month</button>
                        <button
                          type="button"
                          onClick={() => setF2PeriodType('date_range')}
                          className={`px-3 py-1 text-sm ${f2PeriodType === 'date_range' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}
                          data-testid="f2-period-range"
                        >Date Range</button>
                      </div>
                      {/* Pickers */}
                      {f2PeriodType === 'single_month' ? (
                        <input
                          type="month"
                          value={f2SelectedMonth}
                          onChange={(e) => setF2SelectedMonth(e.target.value)}
                          className="border rounded-md px-2 py-1 text-sm"
                          data-testid="f2-selected-month"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <input type="month" value={f2FromMonth} onChange={(e) => setF2FromMonth(e.target.value)} className="border rounded-md px-2 py-1 text-sm" data-testid="f2-from-month" />
                          <span className="text-xs text-muted-foreground">to</span>
                          <input type="month" value={f2ToMonth} onChange={(e) => setF2ToMonth(e.target.value)} className="border rounded-md px-2 py-1 text-sm" data-testid="f2-to-month" />
                        </div>
                      )}
                      <Button variant="outline" size="sm" onClick={handleF2Export} data-testid="f2-export-excel-btn">
                        <Download className="w-4 h-4 mr-2" /> Export
                      </Button>
                    </div>
                  </div>

                  {f2Loading ? (
                    <div className="text-center py-10 text-muted-foreground text-sm" data-testid="f2-loading">Loading…</div>
                  ) : !f2Matrix ? (
                    <div className="text-center py-10 text-muted-foreground text-sm">No data.</div>
                  ) : (
                    <div className="overflow-x-auto" data-testid="f2-table-container">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="sticky left-0 bg-white z-10 min-w-[180px]">Classification</TableHead>
                            {(f2Matrix.months || []).map((m) => (
                              <TableHead key={m.month_key} className="text-right min-w-[130px]">{m.month_label}</TableHead>
                            ))}
                            <TableHead className="text-right min-w-[120px] bg-amber-50">Pending Production</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(f2Matrix.rows || []).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={(f2Matrix.months?.length || 0) + 2} className="text-center text-muted-foreground py-8">
                                No classifications match the current filters.
                              </TableCell>
                            </TableRow>
                          ) : (f2Matrix.rows || []).map((row) => (
                            <TableRow key={row.classification} data-testid={`f2-row-${row.classification}`}>
                              <TableCell className="font-medium sticky left-0 bg-white z-10">{row.classification}</TableCell>
                              {(f2Matrix.months || []).map((m) => {
                                const c = row.cells.find((cc) => cc.month_key === m.month_key) || { order: 0, production: 0, dispatched: 0 };
                                const fmt = (v) => (v > 0 ? (f2Mode === 'value' ? `${(v / 1e7).toFixed(2)} Cr` : formatQuantity ? formatQuantity(v) : v.toLocaleString()) : '—');
                                return (
                                  <TableCell key={m.month_key} className="text-right font-mono text-xs align-top">
                                    <div className="flex flex-col items-end gap-0.5">
                                      <div><span className="text-[10px] text-muted-foreground mr-1">O</span>{fmt(c.order)}</div>
                                      <div><span className="text-[10px] text-muted-foreground mr-1">P</span>{fmt(c.production)}</div>
                                      <div><span className="text-[10px] text-muted-foreground mr-1">D</span>{fmt(c.dispatched)}</div>
                                    </div>
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-semibold bg-amber-50">
                                {row.pending_production > 0
                                  ? (f2Mode === 'value' ? `${(row.pending_production / 1e7).toFixed(2)} Cr` : (formatQuantity ? formatQuantity(row.pending_production) : row.pending_production.toLocaleString()))
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Totals row */}
                          {f2Matrix.totals && (
                            <TableRow className="bg-gray-100 font-semibold" data-testid="f2-totals-row">
                              <TableCell className="sticky left-0 bg-gray-100 z-10">Totals</TableCell>
                              {(f2Matrix.months || []).map((m) => {
                                const c = (f2Matrix.totals.cells || []).find((cc) => cc.month_key === m.month_key) || { order: 0, production: 0, dispatched: 0 };
                                const fmt = (v) => (v > 0 ? (f2Mode === 'value' ? `${(v / 1e7).toFixed(2)} Cr` : (formatQuantity ? formatQuantity(v) : v.toLocaleString())) : '—');
                                return (
                                  <TableCell key={m.month_key} className="text-right font-mono text-xs align-top">
                                    <div className="flex flex-col items-end gap-0.5">
                                      <div><span className="text-[10px] text-muted-foreground mr-1">O</span>{fmt(c.order)}</div>
                                      <div><span className="text-[10px] text-muted-foreground mr-1">P</span>{fmt(c.production)}</div>
                                      <div><span className="text-[10px] text-muted-foreground mr-1">D</span>{fmt(c.dispatched)}</div>
                                    </div>
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-bold bg-amber-100">
                                {(f2Matrix.totals.pending_production || 0) > 0
                                  ? (f2Mode === 'value' ? `${(f2Matrix.totals.pending_production / 1e7).toFixed(2)} Cr` : (formatQuantity ? formatQuantity(f2Matrix.totals.pending_production) : f2Matrix.totals.pending_production.toLocaleString()))
                                  : '—'}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Profitability by Classification */}
                {fngProfitabilityData && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Profitability by Classification</h3>
                      <div className="flex gap-2">
                        <Select value={fngProfitPeriod} onValueChange={(v) => { setFngProfitPeriod(v); fetchProductsIntelligence(); }}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ytd">YTD</SelectItem>
                            <SelectItem value="mtd">MTD</SelectItem>
                            <SelectItem value="qtd">QTD</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={fngSortOrder} onValueChange={(v) => { setFngSortOrder(v); fetchProductsIntelligence(); }}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top">Top</SelectItem>
                            <SelectItem value="bottom">Bottom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={fngProfitabilityData.data?.slice(0, 10) || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)}%`} />
                          <YAxis dataKey="classification" type="category" width={120} />
                          <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                          <Bar dataKey="profit_pct" name="Profit Margin" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Client Performance */}
                {clientPerformanceData && (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Client Performance</h3>
                      <div className="flex gap-2">
                        <Select value={clientPeriod} onValueChange={(v) => { setClientPeriod(v); fetchProductsIntelligence(); }}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ytd">YTD</SelectItem>
                            <SelectItem value="mtd">MTD</SelectItem>
                            <SelectItem value="qtd">QTD</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={clientSortOrder} onValueChange={(v) => { setClientSortOrder(v); fetchProductsIntelligence(); }}>
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top">Top</SelectItem>
                            <SelectItem value="bottom">Bottom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Client</TableHead>
                          <TableHead className="text-right">Orders</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clientPerformanceData.data?.slice(0, 10).map((c, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{c.client_name || 'Unknown'}</TableCell>
                            <TableCell className="text-right">{c.orders_count || 0}</TableCell>
                            <TableCell className="text-right">₹{((c.revenue || 0)/100000).toFixed(2)}L</TableCell>
                            <TableCell className="text-right">₹{((c.profit || 0)/100000).toFixed(2)}L</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {!fngProfitabilityData && !clientPerformanceData && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-2" />
                    <p>No product data available</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* PO View Dialog */}
      <Dialog open={viewPODialog} onOpenChange={(o) => { setViewPODialog(o); if (!o) setLineItemLastPrices({}); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Order Details - {selectedPO?.po_number}</DialogTitle>
            <DialogDescription>Review PO details before approval</DialogDescription>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-6 py-4">
              {/* PO Header Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">{selectedPO.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">PO Date</p>
                  <p className="font-medium">{new Date(selectedPO.po_date).toLocaleDateString('en-GB')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expected Delivery</p>
                  <p className="font-medium">
                    {selectedPO.expected_delivery_date 
                      ? new Date(selectedPO.expected_delivery_date).toLocaleDateString('en-GB') 
                      : 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submitted By</p>
                  <p className="font-medium">{selectedPO.submitted_by_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submitted On</p>
                  <p className="font-medium">{new Date(selectedPO.submitted_for_approval_at).toLocaleString('en-GB')}</p>
                </div>
                {selectedPO.notes && (
                  <div>
                    <p className="text-sm text-muted-foreground">Notes</p>
                    <p className="font-medium">{selectedPO.notes}</p>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Item</TableHead>
                      <TableHead>SO No.</TableHead>
                      <TableHead className="text-right">PO Qty</TableHead>
                      <TableHead className="text-right">Supplier Qty</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Last Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center w-16">History</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPO.line_items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>
                          {/* Support both so_numbers array and legacy so_number string */}
                          {(item.so_numbers && item.so_numbers.length > 0) ? (
                            <div className="flex flex-wrap gap-1">
                              {item.so_numbers.map((so, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {so}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              {item.so_number || 'Stock'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {item.system_qty_ordered || item.indent_quantity} {item.indent_unit}
                        </TableCell>
                        <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell className="text-right">{formatRate(item.unit_price)}</TableCell>
                        <TableCell className="text-right text-xs">
                          {(() => {
                            const lp = lineItemLastPrices[item.item_id];
                            if (!lp || lp.price == null) return <span className="text-gray-400">-</span>;
                            const cur = parseFloat(item.unit_price) || 0;
                            const delta = cur - lp.price;
                            const pct = lp.price > 0 ? (delta / lp.price) * 100 : 0;
                            const color = pct <= 0 ? 'text-green-700' : (pct <= 5 ? 'text-yellow-700' : 'text-red-700');
                            return (
                              <div className="flex flex-col items-end" data-testid={`dashboard-po-last-price-${item.item_id}`}>
                                <span className="font-medium">₹{lp.price.toFixed(2)}</span>
                                <span className={`text-[10px] ${color}`}>
                                  {delta >= 0 ? '+' : ''}{pct.toFixed(1)}%
                                  {lp.source === 'avg_all' && <span className="text-gray-500"> (avg)</span>}
                                </span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              setPriceHistoryItem({
                                itemId: item.item_id,
                                itemName: item.item_name,
                                supplierId: selectedPO.supplier_id,
                                supplierName: selectedPO.supplier_name,
                                currentPrice: parseFloat(item.unit_price) || 0,
                                unit: item.unit
                              });
                              setPriceHistoryOpen(true);
                            }}
                            title="View Price History"
                          >
                            <History className="h-4 w-4 text-blue-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Sub Total:</span>
                      <span className="font-medium">{formatCurrency(selectedPO.sub_total)}</span>
                    </div>
                    {selectedPO.other_charges_taxable > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Other Charges (Taxable):</span>
                        <span className="font-medium">{formatCurrency(selectedPO.other_charges_taxable)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>GST ({selectedPO.gst_rate || 0}%):</span>
                      <span className="font-medium">{formatCurrency(selectedPO.gst_amount)}</span>
                    </div>
                    {selectedPO.other_charges_non_taxable > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Other Charges (Non-Taxable):</span>
                        <span className="font-medium">{formatCurrency(selectedPO.other_charges_non_taxable)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Grand Total</p>
                      <p className="text-3xl font-bold text-primary">
                        {formatCurrency(selectedPO.total_amount)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advance Payment Request Display */}
              {selectedPO.advance_requested && selectedPO.advance_amount > 0 && (
                <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <IndianRupee className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">Advance Payment Requested</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-amber-700">Amount</p>
                      <p className="text-lg font-bold text-amber-900">
                        {formatCurrency(selectedPO.advance_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-amber-700">Due Date</p>
                      <p className="font-medium text-amber-900">
                        {selectedPO.advance_payment_due_date 
                          ? new Date(selectedPO.advance_payment_due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                          : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-amber-700">% of PO Value</p>
                      <p className="font-medium text-amber-900">
                        {((selectedPO.advance_amount / selectedPO.total_amount) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    * A payment request will be auto-created when this PO is approved
                  </p>
                </div>
              )}

              {/* Clarification History */}
              {selectedPO.clarification_history && selectedPO.clarification_history.length > 0 && (
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Clarification History</span>
                  </div>
                  <div className="space-y-3">
                    {selectedPO.clarification_history.map((entry, idx) => (
                      <div key={entry.id || idx} className="border-l-2 border-blue-300 pl-3 py-2">
                        <div className="bg-white rounded p-2 mb-2">
                          <p className="text-xs text-blue-600 font-medium">
                            {entry.requested_by_name} requested clarification on {new Date(entry.requested_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-sm mt-1">{entry.remarks}</p>
                        </div>
                        {entry.response && (
                          <div className="bg-green-50 rounded p-2 ml-4">
                            <p className="text-xs text-green-600 font-medium">
                              Response on {new Date(entry.responded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-sm mt-1">{entry.response}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrintPO(selectedPO)}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" onClick={() => { setViewPODialog(false); setLineItemLastPrices({}); }}>Close</Button>
            <Button 
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => openClarificationDialog('po', selectedPO.id, selectedPO.po_number)}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Request Clarification
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handlePOApproval(selectedPO.id, 'approve')}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve PO
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                const reason = prompt('Enter rejection reason:');
                if (reason) handlePOApproval(selectedPO.id, 'reject', reason);
              }}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SPO View Dialog */}
      <Dialog open={viewSPODialog} onOpenChange={setViewSPODialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Service PO - {selectedSPO?.spo_number}</DialogTitle>
            <DialogDescription>Review service purchase order details</DialogDescription>
          </DialogHeader>
          
          {selectedSPO && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">{selectedSPO.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submitted By</p>
                  <p className="font-medium">{selectedSPO.submitted_by_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Service Description</p>
                  <p className="font-medium">{selectedSPO.service_description}</p>
                </div>
              </div>

              {/* Service Items */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Service Items</p>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedSPO.service_items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="text-right">{formatRate(item.rate)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-muted/30 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span>Sub Total:</span>
                  <span>{formatCurrency(selectedSPO.sub_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST ({selectedSPO.gst_rate}%):</span>
                  <span>{formatCurrency(selectedSPO.gst_amount)}</span>
                </div>
                {selectedSPO.other_charges > 0 && (
                  <div className="flex justify-between">
                    <span>Other Charges:</span>
                    <span>{formatCurrency(selectedSPO.other_charges)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Grand Total:</span>
                  <span>{formatCurrency(selectedSPO.grand_total)}</span>
                </div>
              </div>

              {/* Advance Payment Info */}
              {selectedSPO.advance_requested && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <p className="text-sm font-medium text-orange-800 mb-2">Advance Payment Requested</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-orange-600">Amount</p>
                      <p className="font-bold text-orange-800">{formatCurrency(selectedSPO.advance_amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-orange-600">Due Date</p>
                      <p className="font-medium text-orange-800">
                        {selectedSPO.advance_payment_due_date 
                          ? new Date(selectedSPO.advance_payment_due_date).toLocaleDateString('en-GB') 
                          : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-orange-600 mt-2">
                    * Approving this SPO will auto-create an advance payment request
                  </p>
                </div>
              )}

              {selectedSPO.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="font-medium">{selectedSPO.notes}</p>
                </div>
              )}

              {/* Clarification History */}
              {selectedSPO.clarification_history && selectedSPO.clarification_history.length > 0 && (
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Clarification History</span>
                  </div>
                  <div className="space-y-3">
                    {selectedSPO.clarification_history.map((entry, idx) => (
                      <div key={entry.id || idx} className="border-l-2 border-blue-300 pl-3 py-2">
                        <div className="bg-white rounded p-2 mb-2">
                          <p className="text-xs text-blue-600 font-medium">
                            {entry.requested_by_name} requested clarification on {new Date(entry.requested_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-sm mt-1">{entry.remarks}</p>
                        </div>
                        {entry.response && (
                          <div className="bg-green-50 rounded p-2 ml-4">
                            <p className="text-xs text-green-600 font-medium">
                              Response on {new Date(entry.responded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                            <p className="text-sm mt-1">{entry.response}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewSPODialog(false)}>Close</Button>
            <Button 
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => openClarificationDialog('spo', selectedSPO.id, selectedSPO.spo_number)}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Request Clarification
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleSPOApproval(selectedSPO.id, 'approve')}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve SPO
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                const reason = prompt('Enter rejection reason:');
                if (reason) handleSPOApproval(selectedSPO.id, 'reject', reason);
              }}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment View Dialog */}
      <Dialog open={viewPaymentDialog} onOpenChange={setViewPaymentDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Request - {selectedPayment?.request_number}</DialogTitle>
            <DialogDescription>Review payment request details</DialogDescription>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">{selectedPayment.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Type</p>
                  <Badge>{selectedPayment.payment_type}</Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount Requested</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayment.amount_requested)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className="font-medium">
                    {selectedPayment.payment_due_date 
                      ? new Date(selectedPayment.payment_due_date).toLocaleDateString('en-GB') 
                      : 'Not specified'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Purpose</p>
                  <p className="font-medium">{selectedPayment.purpose || '-'}</p>
                </div>
                {selectedPayment.source_po_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Linked PO</p>
                    <p className="font-medium">{selectedPayment.source_po_number}</p>
                  </div>
                )}
                {selectedPayment.source_spo_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Linked Service PO</p>
                    <p className="font-medium">{selectedPayment.source_spo_number}</p>
                  </div>
                )}
                {selectedPayment.source_srn_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Linked SRN</p>
                    <p className="font-medium">{selectedPayment.source_srn_number}</p>
                  </div>
                )}
                {selectedPayment.source_grn_no && (
                  <div>
                    <p className="text-sm text-muted-foreground">Linked GRN</p>
                    <p className="font-medium">{selectedPayment.source_grn_no}</p>
                  </div>
                )}
              </div>

              {/* GRN Summary Section */}
              {loadingGrnDetails ? (
                <div className="text-center py-4 text-muted-foreground">
                  <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
                  <p className="text-sm">Loading GRN details...</p>
                </div>
              ) : paymentGrnDetails.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-700">GRN Summary</span>
                    <Badge variant="outline" className="ml-auto">{paymentGrnDetails.length} GRN(s)</Badge>
                  </div>
                  
                  {paymentGrnDetails.map((grn, grnIdx) => (
                    <div key={grn.id || grnIdx} className={grnIdx > 0 ? 'border-t' : ''}>
                      {/* GRN Header */}
                      <div className="grid grid-cols-4 gap-3 p-3 bg-muted/30">
                        <div>
                          <span className="text-xs text-muted-foreground">GRN No</span>
                          <p className="font-mono text-sm font-medium">{grn.store_receipt_no || grn.grn_number || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">Invoice No</span>
                          <p className="font-mono text-sm">{grn.invoice_no || grn.invoice_dc_no || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">PO Number</span>
                          <p className="font-mono text-sm">{grn.po_number || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground">GRN Date</span>
                          <p className="text-sm">{grn.created_at ? new Date(grn.created_at).toLocaleDateString('en-GB') : '-'}</p>
                        </div>
                      </div>
                      
                      {/* Line Items Table */}
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/20">
                            <TableHead className="text-xs">Item</TableHead>
                            <TableHead className="text-xs text-right">Qty</TableHead>
                            <TableHead className="text-xs text-right">Rate</TableHead>
                            <TableHead className="text-xs text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(grn.items || []).map((item, idx) => (
                            <TableRow key={idx} className="text-sm">
                              <TableCell className="py-2">{item.item_name}</TableCell>
                              <TableCell className="py-2 text-right">
                                {item.supplier_qty || item.system_qty || item.quantity_received || 0} {item.supplier_unit || item.unit || ''}
                              </TableCell>
                              <TableCell className="py-2 text-right">
                                {formatRate(item.supplier_rate || item.system_rate || 0)}
                              </TableCell>
                              <TableCell className="py-2 text-right font-medium">
                                {formatCurrency(item.supplier_amount || item.system_amount || 0)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {/* GRN Totals */}
                      <div className="flex justify-end p-3 bg-muted/20">
                        <div className="w-64 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span>{formatCurrency(grn.total_supplier_amount || grn.total_system_amount || 0)}</span>
                          </div>
                          {(grn.other_charges_taxable || 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Other Charges (Taxable)</span>
                              <span>{formatCurrency(grn.other_charges_taxable)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">GST ({grn.gst_rate || 0}%)</span>
                            <span>{formatCurrency(grn.gst_amount || 0)}</span>
                          </div>
                          {(grn.other_charges_non_taxable || 0) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Other Charges (Non-Taxable)</span>
                              <span>{formatCurrency(grn.other_charges_non_taxable)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold border-t pt-1">
                            <span>Grand Total</span>
                            <span>{formatCurrency(grn.grand_total || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Modification History - Show if modifications exist */}
              {selectedPayment.modifications?.length > 0 && (
                <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                  <p className="text-sm font-medium text-amber-800 mb-3">Modification History</p>
                  <div className="space-y-3">
                    {selectedPayment.modifications.map((mod, idx) => (
                      <div key={idx} className="bg-white rounded p-3 border border-amber-100">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs text-amber-600">
                            {mod.requested_by} - {mod.requested_at ? new Date(mod.requested_at).toLocaleString('en-GB') : ''}
                          </span>
                          <Badge variant="outline" className={mod.approved === true ? 'bg-green-50 text-green-700' : mod.approved === false ? 'bg-red-50 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {mod.approved === true ? 'Approved' : mod.approved === false ? 'Rejected' : 'Pending'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Original</p>
                            <p className="font-medium">{mod.from_amount ? formatCurrency(mod.from_amount) : '-'}</p>
                            <p className="text-xs text-gray-500">{mod.from_date ? new Date(mod.from_date).toLocaleDateString('en-GB') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Requested Change</p>
                            <p className="font-medium text-amber-700">{mod.new_amount ? formatCurrency(mod.new_amount) : '-'}</p>
                            <p className="text-xs text-amber-600">{mod.new_date ? new Date(mod.new_date).toLocaleDateString('en-GB') : '-'}</p>
                          </div>
                        </div>
                        <p className="text-xs mt-2 text-gray-600"><strong>Reason:</strong> {mod.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Split From Info */}
              {selectedPayment.split_from_number && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <strong>Split from:</strong> {selectedPayment.split_from_number}
                  </p>
                </div>
              )}

              {/* Split Into Info */}
              {selectedPayment.status === 'split' && selectedPayment.split_into?.length > 0 && (
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-700 font-medium mb-2">Split into {selectedPayment.split_into.length} payments:</p>
                  <div className="space-y-1">
                    {selectedPayment.split_into.map((s, idx) => (
                      <p key={idx} className="text-sm text-purple-600">
                        {s.number}: {formatCurrency(s.amount)} - Due: {new Date(s.due_date).toLocaleDateString('en-GB')}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setViewPaymentDialog(false)}>Close</Button>
            {selectedPayment?.status !== 'split' && selectedPayment?.status !== 'paid' && (
              <>
                <Button
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    setViewPaymentDialog(false);
                    setSplitPaymentId(selectedPayment.id);
                    setSplitAmount(selectedPayment.amount_requested);
                    setSplits([
                      { amount: Math.floor(selectedPayment.amount_requested / 2), due_date: '' },
                      { amount: selectedPayment.amount_requested - Math.floor(selectedPayment.amount_requested / 2), due_date: '' }
                    ]);
                    setSplitDialogOpen(true);
                  }}
                >
                  <Scissors className="w-4 h-4 mr-2" />
                  Split Payment
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handlePaymentApproval(selectedPayment.id, 'approve', { approved_amount: selectedPayment.amount_requested }, selectedPayment.status)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedPayment?.status === 'modification_requested' ? 'Approve Modification' : 'Approve'}
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    const reason = prompt('Enter rejection reason:');
                    if (reason) handlePaymentApproval(selectedPayment.id, 'reject', { admin_remarks: reason }, selectedPayment.status);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Payment Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Split Payment</DialogTitle>
            <DialogDescription>
              Split {formatCurrency(splitAmount)} into multiple payments with different due dates
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Original Amount: <strong>{formatCurrency(splitAmount)}</strong></p>
              <p className="text-sm text-gray-600">Total Split: <strong className={Math.abs(splits.reduce((s, x) => s + (x.amount || 0), 0) - splitAmount) < 1 ? 'text-green-600' : 'text-red-600'}>
                {formatCurrency(splits.reduce((s, x) => s + (x.amount || 0), 0))}
              </strong></p>
            </div>

            <div className="space-y-3">
              {splits.map((split, idx) => (
                <div key={idx} className="flex gap-3 items-end p-3 border rounded-lg">
                  <div className="flex-1">
                    <Label className="text-xs">Amount (₹)</Label>
                    <Input
                      type="number"
                      value={split.amount}
                      onChange={(e) => {
                        const newSplits = [...splits];
                        newSplits[idx].amount = parseFloat(e.target.value) || 0;
                        setSplits(newSplits);
                      }}
                      min="0"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Due Date</Label>
                    <Input
                      type="date"
                      value={split.due_date}
                      onChange={(e) => {
                        const newSplits = [...splits];
                        newSplits[idx].due_date = e.target.value;
                        setSplits(newSplits);
                      }}
                    />
                  </div>
                  {splits.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500"
                      onClick={() => setSplits(splits.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setSplits([...splits, { amount: 0, due_date: '' }])}
            >
              <Plus className="w-4 h-4 mr-1" /> Add Split
            </Button>

            <div className="space-y-2">
              <Label>Reason for Split</Label>
              <Textarea
                value={splitReason}
                onChange={(e) => setSplitReason(e.target.value.toUpperCase())}
                placeholder="Enter reason for splitting payment..."
                className="uppercase"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSplitDialogOpen(false); setSplitPaymentId(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSplitPayment}
              disabled={
                !splitReason ||
                splits.some(s => !s.amount || !s.due_date) ||
                Math.abs(splits.reduce((sum, s) => sum + s.amount, 0) - splitAmount) > 0.01
              }
            >
              <Scissors className="w-4 h-4 mr-2" />
              Confirm Split
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Print Component */}
      {printPO && (
        <POPrint
          po={printPO}
          autoPrint={true}
          onPrintComplete={() => setPrintPO(null)}
        />
      )}

      {/* Price History Popup */}
      <PriceHistoryPopup
        open={priceHistoryOpen}
        onClose={() => {
          setPriceHistoryOpen(false);
          setPriceHistoryItem(null);
        }}
        itemId={priceHistoryItem?.itemId}
        itemName={priceHistoryItem?.itemName}
        supplierId={priceHistoryItem?.supplierId}
        supplierName={priceHistoryItem?.supplierName}
        currentPrice={priceHistoryItem?.currentPrice}
        unit={priceHistoryItem?.unit}
      />

      {/* Combined CA/PA Assignment Dialog */}
      <Dialog open={assignCAPADialog} onOpenChange={setAssignCAPADialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {selectedComplaint?.complaint_no}: {selectedComplaint?.title}
            </DialogTitle>
            <DialogDescription>
              Raised by {selectedComplaint?.raised_by_name} on {selectedComplaint?.raised_date ? new Date(selectedComplaint.raised_date).toLocaleDateString('en-GB') : '-'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedComplaint && (
            <div className="space-y-6 py-4">
              {/* Complaint Info Section */}
              <div className="border rounded-lg p-4 bg-gray-50/50">
                <h4 className="font-semibold mb-3 text-sm">Complaint Details</h4>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="bg-white p-2 rounded border">
                    <p className="text-xs text-muted-foreground">Source</p>
                    <p className="font-medium capitalize text-sm">{selectedComplaint.source}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-xs text-muted-foreground">Severity</p>
                    <Badge className={
                      selectedComplaint.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      selectedComplaint.severity === 'major' ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    }>
                      {selectedComplaint.severity === 'critical' ? '🔴' : selectedComplaint.severity === 'major' ? '🟠' : '🟡'} {selectedComplaint.severity}
                    </Badge>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="font-medium text-sm">{selectedComplaint.category}</p>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge variant="outline" className="capitalize text-xs">{selectedComplaint.status?.replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border mb-3">
                  <p className="text-xs text-muted-foreground mb-1">Description</p>
                  <p className="text-sm">{selectedComplaint.description}</p>
                </div>
                
                {(selectedComplaint.work_order_number || selectedComplaint.process_name || selectedComplaint.customer_name) && (
                  <div className="flex gap-2 flex-wrap">
                    {selectedComplaint.work_order_number && (
                      <Badge variant="secondary" className="text-xs">WO: {selectedComplaint.work_order_number}</Badge>
                    )}
                    {selectedComplaint.process_name && (
                      <Badge variant="secondary" className="text-xs">Process: {selectedComplaint.process_name}</Badge>
                    )}
                    {selectedComplaint.customer_name && (
                      <Badge variant="secondary" className="text-xs">Customer: {selectedComplaint.customer_name}</Badge>
                    )}
                  </div>
                )}
                
                {/* Production Personnel Info */}
                {selectedComplaint.work_order_id && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-muted-foreground">Production Supervisor (Issued By)</p>
                      <p className="font-medium text-sm">{complaintWOData?.supervisor_name || 'N/A'}</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-xs text-muted-foreground">Worker</p>
                      <p className="font-medium text-sm">
                        {complaintWOData?.worker_name || 'N/A'}
                        {complaintWOData?.additional_workers?.length > 0 && (
                          <span className="text-muted-foreground ml-1">(+{complaintWOData.additional_workers.length} more)</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Images Section */}
              {selectedComplaint.images && selectedComplaint.images.length > 0 && (
                <div className="border rounded-lg p-4 bg-blue-50/30">
                  <h4 className="font-semibold mb-3 text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Attached Images ({selectedComplaint.images.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedComplaint.images.map((img, idx) => (
                      <a key={idx} href={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} 
                          alt={`Complaint image ${idx + 1}`} 
                          className="w-full h-24 object-cover rounded border hover:opacity-80 cursor-pointer"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* CA Assignment Section */}
              <div className="border-2 border-yellow-300 rounded-lg p-4 bg-yellow-50/30">
                <h4 className="font-semibold mb-3 text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Corrective Action (CA) Assignment
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Assign To *</Label>
                      <Select value={capaForm.ca_assigned_to_id} onValueChange={(v) => {
                        const user = capaUsers.find(u => u.id === v);
                        setCapaForm({ ...capaForm, ca_assigned_to_id: v, ca_assigned_to_name: user?.name || '' });
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select person" /></SelectTrigger>
                        <SelectContent>
                          {capaUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role || u.department || 'User'})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Target Date *</Label>
                      <Input
                        type="date"
                        value={capaForm.ca_target_date}
                        onChange={(e) => setCapaForm({ ...capaForm, ca_target_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* PA Assignment Section */}
              <div className="border-2 border-indigo-300 rounded-lg p-4 bg-indigo-50/30">
                <h4 className="font-semibold mb-3 text-indigo-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Preventive Action (PA) Assignment
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Assign To *</Label>
                      <Select value={capaForm.pa_assigned_to_id} onValueChange={(v) => {
                        const user = capaUsers.find(u => u.id === v);
                        setCapaForm({ ...capaForm, pa_assigned_to_id: v, pa_assigned_to_name: user?.name || '' });
                      }}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select person" /></SelectTrigger>
                        <SelectContent>
                          {capaUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role || u.department || 'User'})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Target Date *</Label>
                      <Input
                        type="date"
                        value={capaForm.pa_target_date}
                        onChange={(e) => setCapaForm({ ...capaForm, pa_target_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600 italic">
                    Note: The assignee will fill RCA and Preventive Action details when completing the task.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setAssignCAPADialog(false)}>Cancel</Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              disabled={submittingCapa || 
                !capaForm.ca_assigned_to_id || !capaForm.ca_target_date ||
                !capaForm.pa_assigned_to_id || !capaForm.pa_target_date
              }
              onClick={async () => {
                setSubmittingCapa(true);
                try {
                  await axios.post(`${API_URL}/complaints/${selectedComplaint.id}/assign-capa`, capaForm, { headers: getAuthHeader() });
                  toast.success('CA & PA assigned successfully with Critical priority');
                  setAssignCAPADialog(false);
                  fetchPendingCAPA();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to assign CA & PA');
                } finally {
                  setSubmittingCapa(false);
                }
              }}
            >
              {submittingCapa ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign CA & PA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clarification Request Dialog */}
      <Dialog open={clarificationDialog.open} onOpenChange={(open) => {
        if (!open) setClarificationDialog({ open: false, type: null, id: null, number: null });
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Clarification</DialogTitle>
            <DialogDescription>
              Send clarification request for {clarificationDialog.type === 'po' ? 'PO' : 'SPO'} {clarificationDialog.number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="clarification-remarks">Clarification Remarks *</Label>
            <Textarea
              id="clarification-remarks"
              placeholder="Enter your questions or clarification needed..."
              value={clarificationRemarks}
              onChange={(e) => setClarificationRemarks(e.target.value)}
              className="mt-2 min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This will be sent to the creator who will respond and resubmit for your review.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setClarificationDialog({ open: false, type: null, id: null, number: null })}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleRequestClarification}
              disabled={!clarificationRemarks.trim()}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Clarification Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Request Detail Dialog */}
      <Dialog open={viewEmpRequestDialog} onOpenChange={(open) => { if (!open) { setViewEmpRequestDialog(false); setSelectedEmpRequest(null); setEmpDialogMode('view'); setEmpDialogInput(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Details — {selectedEmpRequest?.request_number}</DialogTitle>
            <DialogDescription>
              Submitted by {selectedEmpRequest?.employee_name} on {selectedEmpRequest ? new Date(selectedEmpRequest.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedEmpRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{
                  {regular_leave:'Regular Leave',emergency_leave:'Emergency Leave',paid_leave:'Paid Leave',overtime:'Overtime',reimbursement:'Reimbursement',permission:'Short Leave',advance:'Advance'}[selectedEmpRequest.type] || selectedEmpRequest.type
                }</span></div>
                <div><span className="text-muted-foreground">Status:</span> <Badge className="ml-1 bg-yellow-100 text-yellow-800 border-yellow-300">Pending</Badge></div>
                {selectedEmpRequest.type?.includes('leave') && (<>
                  <div><span className="text-muted-foreground">From:</span> {new Date(selectedEmpRequest.from_date).toLocaleDateString('en-GB')}</div>
                  <div><span className="text-muted-foreground">To:</span> {new Date(selectedEmpRequest.to_date).toLocaleDateString('en-GB')} ({selectedEmpRequest.num_days} days)</div>
                  {selectedEmpRequest.coverage_backup_name && (
                    <div className="col-span-2"><span className="text-muted-foreground">Coverage Backup:</span> <span className="font-medium text-blue-600">{selectedEmpRequest.coverage_backup_name}</span></div>
                  )}
                </>)}
                {selectedEmpRequest.type === 'overtime' && (<>
                  <div><span className="text-muted-foreground">OT Date:</span> {new Date(selectedEmpRequest.ot_date).toLocaleDateString('en-GB')}</div>
                  <div><span className="text-muted-foreground">Time:</span> {selectedEmpRequest.from_time} - {selectedEmpRequest.to_time} ({selectedEmpRequest.hours} hrs)</div>
                </>)}
                {selectedEmpRequest.type === 'permission' && (<>
                  <div><span className="text-muted-foreground">Date:</span> {new Date(selectedEmpRequest.date).toLocaleDateString('en-GB')}</div>
                  <div><span className="text-muted-foreground">Time:</span> {selectedEmpRequest.from_time} - {selectedEmpRequest.to_time} ({selectedEmpRequest.hours} hrs)</div>
                </>)}
                {selectedEmpRequest.type === 'advance' && (<>
                  <div><span className="text-muted-foreground">Advance Type:</span> {selectedEmpRequest.advance_type === 'salary' ? 'Salary' : 'Travel'}</div>
                  <div><span className="text-muted-foreground">Amount:</span> ₹{selectedEmpRequest.amount?.toLocaleString()}</div>
                  {selectedEmpRequest.expected_return_date && <div className="col-span-2"><span className="text-muted-foreground">Expected Return:</span> {new Date(selectedEmpRequest.expected_return_date).toLocaleDateString('en-GB')}</div>}
                </>)}
                {selectedEmpRequest.type === 'reimbursement' && (<>
                  <div><span className="text-muted-foreground">Total:</span> ₹{selectedEmpRequest.total_amount?.toLocaleString()}</div>
                  <div><span className="text-muted-foreground">Invoice:</span> {selectedEmpRequest.has_invoice ? 'Yes' : 'No'}</div>
                </>)}
              </div>
              {selectedEmpRequest.reason && (
                <div className="text-sm"><span className="text-muted-foreground">Reason:</span> <span>{selectedEmpRequest.reason}</span></div>
              )}
              {selectedEmpRequest.type === 'reimbursement' && selectedEmpRequest.items?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader><TableRow className="bg-muted/30"><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {selectedEmpRequest.items.map((item, idx) => (
                        <TableRow key={idx}><TableCell className="text-sm">{new Date(item.date).toLocaleDateString('en-GB')}</TableCell><TableCell className="text-sm">{item.description}</TableCell><TableCell className="text-sm text-right">₹{item.amount?.toLocaleString()}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {selectedEmpRequest.clarification_thread?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Clarification Thread</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-muted/20">
                    {selectedEmpRequest.clarification_thread.map((entry, idx) => (
                      <div key={idx} className={`p-2 rounded-lg text-sm ${entry.role === 'approver' ? 'bg-blue-50 border-l-2 border-blue-400' : 'bg-green-50 border-l-2 border-green-400'}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-xs">{entry.from_name} ({entry.role === 'approver' ? 'Owner' : 'Employee'})</span>
                          <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p>{entry.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Inline Clarification Input */}
              {empDialogMode === 'clarify' && (
                <div className="space-y-2 border rounded-lg p-3 bg-blue-50/50">
                  <Label className="text-sm font-medium">Ask Clarification</Label>
                  <Textarea
                    value={empDialogInput}
                    onChange={(e) => setEmpDialogInput(e.target.value)}
                    placeholder="What information do you need from the employee?"
                    className="bg-white"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEmpDialogMode('view'); setEmpDialogInput(''); }}>Cancel</Button>
                    <Button size="sm" onClick={() => handleEmpRequestClarification(selectedEmpRequest.id, empDialogInput)}>
                      <Send className="w-4 h-4 mr-1" /> Send
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Inline Rejection Input */}
              {empDialogMode === 'reject' && (
                <div className="space-y-2 border rounded-lg p-3 bg-red-50/50">
                  <Label className="text-sm font-medium text-red-700">Rejection Reason</Label>
                  <Textarea
                    value={empDialogInput}
                    onChange={(e) => setEmpDialogInput(e.target.value)}
                    placeholder="Reason for rejecting this request..."
                    className="bg-white border-red-200"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEmpDialogMode('view'); setEmpDialogInput(''); }}>Cancel</Button>
                    <Button size="sm" variant="destructive" onClick={() => {
                      if (!empDialogInput.trim()) { toast.error('Rejection reason is required'); return; }
                      handleEmpRequestApproval(selectedEmpRequest.id, 'reject', empDialogInput.trim());
                      setViewEmpRequestDialog(false); setEmpDialogMode('view'); setEmpDialogInput('');
                    }}>
                      <XCircle className="w-4 h-4 mr-1" /> Confirm Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {empDialogMode === 'view' && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" size="sm" onClick={() => { setEmpDialogMode('clarify'); setEmpDialogInput(''); }}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Ask Clarification
              </Button>
              <Button variant="destructive" size="sm" onClick={() => { setEmpDialogMode('reject'); setEmpDialogInput(''); }}>
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { handleEmpRequestApproval(selectedEmpRequest?.id, 'approve'); setViewEmpRequestDialog(false); }}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
